import { useState } from "react";
import { Clock, Users, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ORADEA_TOURS, APUSENI_TOURS } from "../data/experiences";
import { formatPrice } from "../i18n/pricing";
import { LocalizedLink } from "../lib/i18nRouting";
import SEO from "../components/site/SEO";
import Reveal from "../components/Reveal";

export default function ExperiencesPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [tab, setTab] = useState("all");
  const items = tab === "oradea" ? ORADEA_TOURS : tab === "apuseni" ? APUSENI_TOURS : [...ORADEA_TOURS, ...APUSENI_TOURS];

  return (
    <div className="bg-bone min-h-screen" data-testid="experiences-page">
      <SEO titleKey="experiences.title" descKey="experiences.description" path="/experiente" />
      <section className="pt-32 lg:pt-44 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-xs uppercase tracking-wider-3 text-moss mb-6 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-moss" />
              {t("experiences_page.eyebrow")}
            </div>
            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl text-forest leading-[1.02]">
              {t("experiences_page.title_a")}<br/><span className="italic">{t("experiences_page.title_b")}</span>.
            </h1>
            <p className="mt-8 max-w-xl text-muted-foreground text-base md:text-lg leading-relaxed">
              {t("experiences_page.intro")}
            </p>
          </Reveal>

          <div className="mt-12 flex gap-2 border-b border-bone-deep" data-testid="exp-tabs">
            {[{ key: "all" },{ key: "oradea" },{ key: "apuseni" }].map((tabDef) => (
              <button
                key={tabDef.key}
                data-testid={`tab-${tabDef.key}`}
                onClick={() => setTab(tabDef.key)}
                className={`px-6 py-4 text-xs uppercase tracking-wider-3 transition-colors ${
                  tab === tabDef.key ? "text-forest border-b-2 border-forest -mb-px" : "text-moss hover:text-forest"
                }`}
              >
                {t(`experiences_page.tabs.${tabDef.key}`)}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {items.map((exp, i) => {
              const price = formatPrice(exp.price, lang, exp.slug);
              const duration = t(`tours.${exp.slug}.duration`);
              const group = t(`tours.${exp.slug}.groupSize`);
              const name = t(`tours.${exp.slug}.name`);
              const short = t(`tours.${exp.slug}.short`);
              return (
                <Reveal key={exp.slug} delay={(i % 3) * 100}>
                  <LocalizedLink to={`/experiente/${exp.slug}`} data-testid={`exp-list-card-${exp.slug}`} className="group block bg-white border border-bone-deep h-full">
                    <div className="relative aspect-[4/5] overflow-hidden bg-stone-warm">
                      <img src={exp.image} alt={name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover cine-img" />
                      <div className="absolute top-4 left-4 bg-bone/95 text-forest text-[10px] uppercase tracking-wider-2 px-3 py-1">{duration}</div>
                      <div className="absolute top-4 right-4 bg-forest text-bone text-[10px] uppercase tracking-wider-2 px-3 py-1">{price} {t("common.per_person")}</div>
                    </div>
                    <div className="p-6 lg:p-8">
                      <div className="text-[10px] uppercase tracking-wider-3 text-terracotta">
                        {exp.category === "oradea" ? "Oradea" : "Apuseni"}
                      </div>
                      <h4 className="font-display text-2xl md:text-3xl text-forest leading-tight mt-2">{name}</h4>
                      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{short}</p>
                      <div className="mt-6 pt-5 border-t border-bone-deep flex items-center justify-between text-[11px] uppercase tracking-wider-2 text-moss">
                        <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" />{duration}</span>
                        <span className="flex items-center gap-2"><Users className="w-3.5 h-3.5" />{group}</span>
                      </div>
                      <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-wider-3 text-forest">
                        {t("experiences_page.card_cta")} <ArrowUpRight className="w-4 h-4" />
                      </div>
                    </div>
                  </LocalizedLink>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
